import { useEffect, useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation } from 'react-router-dom';
import { getCategoryPageData } from '../api/cmsApi';

import TextHeader from '../../../components/textHeader/TextHeader';
import LoadingScreen from '../../../components/shared/loading/LoadingScreen';
import MissingPage from '../../../components/shared/errorMessages/MissingPage';
import DesignsInCollectionGrid from '../../../components/shared/grid/DesignsInCollectionGrid';
import { captureAnalytics } from '../../../utilities/Analytics';
import { Box } from '@mui/material';
import { CMSPageData } from '../types/CMSTypes';

// Import new CMS components
import CMSConsumerCarouselBanner from '../components/banner/CMSConsumerCarouselBanner';
import CMSConsumerFeaturedGallery from '../components/gallery/CMSConsumerFeaturedGallery';
import FeatureBadgesRenderer from '../components/customPage/FeatureBadgesRenderer';

// Keep the old API imports for designs data (not part of CMS)
import { apiGetDesignsInCollection } from '../../../api/apiDesigns';

const HAUS_RE = /\bhaus\b/i;

function CollectionTemplate() {
  const { item_group, collection } = useParams();
  const location = useLocation();

  // States for filters (keeping existing functionality)
  const [applicationsList, setApplicationsList] = useState<string[]>([]);
  const [colourList, setColourList] = useState<number[]>([]);
  const [designList, setDesignList] = useState<number[]>([]);
  const [typeList, setTypeList] = useState<number[]>([]);

  // CMS Data Query - Consumer always uses live mode, searches by collection name
  const {
    isSuccess: cmsSuccess,
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
    refetch: cmsRefetch
  } = useQuery(
    [`cms-consumer-collection-${collection}`],
    () => getCategoryPageData(collection || '', 'live'), // Always live mode for consumer
    {
      staleTime: 600000,
      enabled: !!collection,
    }
  );

  // Get the CMS data (always use live data for consumer)
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  // Feature badges: symbols + hub-authored copy/order live on the page object
  const symbols = cmsData?.symbols ?? (cmsResponse as any)?.symbols ?? null;
  const badgeDescriptions = cmsData?.badge_items ?? [];

  // Keep existing designs API call (not part of CMS)
  const designFilters = {
    filters: {
      item_group: item_group,
      range: collection,
    },
    filter_fields: [
      "application",
      "item_group",
      "range",
      "brand",
      "web_colour",
      "design_style",
      "web_materials",
      "web_shape",
      "web_size",
      "web_type",
      "web_standards",
      "web_composition",
      "web_performance"
    ],
    main_filter_field: null,
    selection_fields: null,
    sort_by_setting: "Alphabetical A-Z",
    off_setter: {
      start: 0,
      end: 1000,
    },
    only_published: true,
    only_in_stock: false,
    limited: true,
  };

  const { data: designsData } = useQuery(
    [`collection-designs-${collection}-${item_group}`],
    () => apiGetDesignsInCollection(designFilters)
  );

  // HAUS set badges are display-only (no button/lightbox); Hertex sets get both
  const isHausCollection = useMemo(() => {
    const products = designsData?.products ?? [];
    if (products.length > 0) {
      return products.some(
        (p: any) =>
          (typeof p.brand === 'string' && HAUS_RE.test(p.brand)) ||
          (typeof p.range === 'string' && HAUS_RE.test(p.range))
      );
    }
    return HAUS_RE.test(collection || cmsData?.page_title || '');
  }, [designsData?.products, collection, cmsData?.page_title]);

  // URL parameter extraction (keeping existing functionality)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const applicationParams = searchParams.getAll('application');
    const colourParams = searchParams.getAll('colours');
    const designParams = searchParams.getAll('design_style');
    const typeParams = searchParams.getAll('types');

    setApplicationsList(applicationParams);
    setColourList(colourParams.map(Number));
    setDesignList(designParams.map(Number));
    setTypeList(typeParams.map(Number));
  }, [location.search]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Analytics tracking
  useEffect(() => {
    if (cmsSuccess && cmsData && collection) {
      const title = cmsData.page_title || `Collection - ${collection}`;
      captureAnalytics('pageview', location.pathname, title);
    }
  }, [location.pathname, collection, cmsSuccess, cmsData]);

  // Debug log for designs data (keeping existing functionality)
  useEffect(() => {
    if (designsData?.products) {
      const applications = Array.from(new Set(designsData.products.map((p: any) => p.application)));
      console.log('Applications found in collection:', applications);
      console.log('Total products:', designsData.products.length);
    }
  }, [designsData]);

  // Determine loading and error states
  const isLoading = cmsLoading;
  const isError = cmsError;
  const isSuccess = cmsSuccess;

  if (!collection) {
    return <MissingPage />;
  }

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && (
        <>
          {/* CMS Consumer Carousel Banner */}
          {cmsData && (
            <CMSConsumerCarouselBanner
              bannerMediaOption={cmsData.consumer_banner_media_option}
              bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
              bannerMediaVideo={cmsData.consumer_banner_media_video}
              bannerMediaVideoLink={cmsData.consumer_banner_media_video_link}
              bannerLinkCategoryId={cmsData.consumer_banner_link_category_id}
              bannerWidth={cmsData.consumer_banner_width}
              bannerHeight={cmsData.consumer_banner_height}
              viewMode="live"
            />
          )}

          {/* Text Header - CMS variant: 85% width, same typography as CMS pages */}
          <TextHeader
            variant="cms"
            heading={cmsData?.page_title || collection}
            content={cmsData?.description || ''}
            isCentered={false}
            marginTop={{ xs: '20px', md: 10 }}
            marginBottom={{ xs: '20px', md: 10 }}
          />

          {/* Feature badges - Hertex sets: badges + lightbox, HAUS sets: display only */}
          <Box
            sx={{
              width: { xs: '85%', md: '85%' },
              mx: 'auto',
              '&:not(:empty)': { mb: 5 },
            }}
          >
            <FeatureBadgesRenderer
              symbols={symbols}
              descriptions={badgeDescriptions}
              maxWidth="100%"
              showLearnMore={!isHausCollection}
              modalTitle="Hertex badges"
            />
          </Box>

          {/* Featured images - same component as the CMS pages */}
          {cmsData?.gallery_media && cmsData.gallery_media.length > 0 && (
            <Box
              sx={{
                width: { xs: '85%', md: '85%' },
                mx: 'auto',
                '& .MuiContainer-root': {
                  maxWidth: '100% !important',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important',
                },
              }}
            >
              <CMSConsumerFeaturedGallery
                featuredCollection={{ ...cmsData, category_id: undefined }}
                hideTextTile
                marginTop={0}
                marginBottom={0}
                viewMode="live"
              />
            </Box>
          )}

          {/* Designs in Collection Grid - 85% wide, 6 columns (not part of CMS) */}
          <DesignsInCollectionGrid
            data={designsData}
            marginTop={6}
            marginBottom={8}
          />
        </>
      )}
    </>
  );
}

export default CollectionTemplate;